import { Component, OnDestroy, OnInit, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, BaseService, PatientService } from '../../services';
import { DialogComponent } from '../dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { PatientDataboxListComponent } from '../patient-databox-list/patient-databox-list.component';
import { HttpParams } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { IdleStateService } from 'src/app/services/idle-state.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild(MatSelect) matSelect: MatSelect;

  // -- State Variables --
  navigationUrl: string;
  updatedTime: any;
  isDropdownOpen: boolean = false;
  activeDropdown: string = '';
  displayTimeline: boolean = false;
  checkboxValue: boolean = false;
  token: any;
  hubidselection: any = [];
  private dataSubscription: Subscription;
  dataLoader: boolean = true;
  isDialogOpen: boolean = true;
  lang: any;

  // -- Filters Data --
  dataStatus: any = [
    { name: 'Sending Data', selected: false },
    { name: 'No Data', selected: false },
  ];

  riskStatus: any = [
    { name: 'No Data', selected: false },
    { name: 'Out Of Bed', selected: false },
    { name: 'No Risk', selected: false },
    { name: 'Mid Risk', selected: false },
    { name: 'High Risk', selected: false },
  ];

  selectedDataStatusCheckboxes: any[] = [];
  selectedRiskStatusCheckboxes: any[] = [];
  selectedDataCheckboxes: number = 0;
  selectedRiskCheckboxes: number = 0;
  dataFilters: any;
  riskFilters: any;

  // -- Pagination & Sorting --
  data: any[] = [];
  @Input() dataToDisplay: any = [];
  @Input() timelineId;

  currentPage = 1;
  itemsPerPage = 10;
  pagesArray: number[] = [];
  totalRows: any;

  sortColumn: any;
  sortOrder: any;
  sortParams: any = {};
  previousSortParams: any = {};

  filterColumn: any;
  filterKey: any;
  filterParams: any = {};
  filtersApplied: boolean = false;
  previousFilterParams: any = {};

  // -- Highlight Helpers --
  hubIdHighlighted: 'asc' | 'desc' | null = null;
  wardHighlighted: 'asc' | 'desc' | null = null;
  BedHighlighted: 'asc' | 'desc' | null = null;
  dataHighlighted: 'asc' | 'desc' | null = null;
  riskHighlighted: 'asc' | 'desc' | null = null;

  // -- Miscellaneous --
  storedCheckboxState: any;
  displayTimelineGraph: any;
  formattingTime: any;
  routerStateSnapshot: any;
  isDataRestored: boolean = false;
  autoTime: any = 5;

  // -- Unpair Variables --
  selectUnpiar: any;
  unPairHub_id: any[] = [];
  hubidData: any[];
  selectedItems: any[] = [];

  // -- Ward Filter Properties --
  isWardFilterOpen: boolean = false;
  selectedWard: string | null = null;
  availableWards: string[] = [];
  allPatientsData: any[] = [];

  // -- Risk Counts for Dashboard --
  riskCounts = {
    high: 0,
    mid: 0,
    safe: 0,
    oob: 0
  };

  constructor(
    private router: Router,
    public datepipe: DatePipe,
    public activeRoute: ActivatedRoute,
    public auth: AuthService,
    public dialog: MatDialog,
    private patientService: PatientService,
    private baseService: BaseService,
    private translate: TranslateService,
    private idleService: IdleStateService
  ) {
    this.routerStateSnapshot = this.router.routerState.snapshot;
    this.dataToDisplay = [];
    this.lang = localStorage.getItem('lang') || 'en';
    this.translate.onLangChange.subscribe((event) => {
      this.lang = event.lang;
    });
  }

  ngOnInit(): void {
    window.addEventListener('storage', (e) => {
      if (e.key === 'base_org') {
        if (!this.router.url.includes(e.newValue)) {
          window.location.href = `/${e.newValue}/dashboard`;
        }
      }
    });

    let existDomain = window.location.href.split("/")[3];
    let baseDoamin = localStorage.getItem('base_org');
    if (existDomain != baseDoamin) {
      this.router.navigateByUrl('/login');
    }

    this.displaydashboardData();
    this.patientService.logoutEvent.subscribe(() => {
      this.isDropdownOpen = false;
    });
    this.patientService.pairDeviceEvent.subscribe(() => {
      this.PairingDevice();
    });
    this.patientService.unpairDeviceEvent.subscribe(() => {
      this.unpairPatients();
    });
  }

  // =============================================================
  // 1. DATA LOADING & REFRESH LOGIC
  // =============================================================

  displaydashboardData(): void {
    if (this.auth.isTokenValid()) {
      const params = this.buildParamsWithoutSorting();
      this.dataLoader = true;
      this.patientService.listOfPatients(params).subscribe((res) => {
        if (res?.data) {
          this.patientService.setversionData(res.Version);
          this.data = res.data.map((item) => {
            return { ...item, isSelected: false };
          });
          // For frontend filtering fallback if needed
          this.allPatientsData = [...this.data];

          this.totalRows = res.totalRows;
          this.updatedTime = res.TimeZone;

          // Initialize Helper Data
          this.calculateRiskCounts(this.data);
          this.initializeWards();
          this.updatePagesArray();
          this.setupAutomaticRefresh();
          this.restoreCheckboxStates();

          this.dataLoader = false;
        } else {
          this.patientService.setversionData('');
          this.dataLoader = false;
        }
      });
    } else {
      this.auth.refreshToken();
    }
  }

  RefreshData(): void {
    if (this.auth.isTokenValid()) {
      this.displaydashboardData();
      this.setupAutomaticRefresh();
      // Force update timestamp display if needed
      // this.updatedTime = new Date(); 
    } else {
      this.auth.refreshToken();
    }
  }

  setupAutomaticRefresh() {
    if (this.dataSubscription) this.dataSubscription.unsubscribe();
    const refreshTimer = JSON.parse(localStorage.getItem('autoRefresh')) || this.autoTime;
    this.dataSubscription = interval(refreshTimer * 60 * 1000).subscribe(() => {
      this.displaydashboardData();
    });
  }

  calculateRiskCounts(data: any[]) {
    this.riskCounts = { high: 0, mid: 0, safe: 0, oob: 0 };
    data.forEach(item => {
      const risk = item.risk_type || '';
      if (risk === 'High Risk') this.riskCounts.high++;
      else if (risk === 'Mid Risk') this.riskCounts.mid++;
      else if (risk === 'No Risk' || risk === 'Safe') this.riskCounts.safe++;
      else if (risk === 'Out Of Bed') this.riskCounts.oob++;
    });
  }

  // =============================================================
  // 2. DEVICE PAIRING & UNPAIRING LOGIC
  // =============================================================

  PairingDevice(): void {
    if (this.auth.isTokenValid()) {
      // Redirect to connector page as per legacy logic
      window.location.href = 'connector';
      localStorage.removeItem('hub_id');
      localStorage.removeItem('hub_description');
    } else {
      this.auth.refreshToken();
    }
  }

  unpairPatients(): void {
    if (this.auth.isTokenValid() && this.isDialogOpen) {
      this.isDialogOpen = true;
      this.isDropdownOpen = false;
      this.patientService.listofhubids().subscribe((res) => {
        if (this.dialog.openDialogs.length > 0) {
          this.dialog.openDialogs[this.dialog.openDialogs.length - 1].close();
        }
        this.isDialogOpen = true;
        if (res.status == 'success') {
          let hubidDataArray = res.hub_ids.map(obj => {
            let [key, value] = Object.entries(obj)[0];
            return `${key} - ${value}`;
          });
          this.hubidData = hubidDataArray;
        }
        let dialogRef: any;
        dialogRef = this.dialog.open(PatientDataboxListComponent, {
          width: '400px',
          minHeight: 'calc(100vh - 80vh)',
          maxHeight: '318px',
          height: 'auto',
          data: {
            hubIds: this.hubidData,
            yesBtn: 'Unpair',
            cancelBtn: 'Cancel',
          },
        });
        const dialogSubscriber = dialogRef?.afterClosed().subscribe((result) => {
          if (result === 'yes') {
            this.confirmationDialog();
          }
        });
      });
      this.isDialogOpen = false;
    } else {
      this.auth.refreshToken();
    }
  }


  confirmationDialog() {
    if (this.auth.isTokenValid()) {
      this.patientService.selectedHubIds.subscribe(selectedUnpair => {
        this.selectUnpiar = selectedUnpair.hub_ids;
      });
      let dialogRef: any;
      dialogRef = this.dialog.open(DialogComponent, {
        width: '477px',
        minHeight: 'calc(100vh - 80vh)',
        maxHeight: '318px',
        height: 'auto',
        data: {
          type: 'alert',
          headerImg: 'assets/images/closeIcon.png',
          buttonsAlign: 'horizantal',
          label: `${this.translate.instant('unpairdailog.label')}
            <b>${this.selectUnpiar.join('<br><br>')}</b>`,
          yesBtn: 'Confirm',
          cancelBtn: 'Cancel',
        },
      });
      const dialogSubscriber = dialogRef?.afterClosed().subscribe((result) => {
        if (result === 'yes') {
          this.patientService.selectedHubIds.subscribe(selectedCheckboxes => {
            this.hubidselection = selectedCheckboxes.hub_ids;
            this.unPairHub_id = this.hubidselection.map(result => result.substring(0, 4).trim());
          });
          let hub_ids = { hub_ids: this.unPairHub_id };
          this.patientService.unpairPatients(hub_ids).subscribe((res) => {
            this.displaydashboardData();
          });
        }
      });
    } else {
      this.auth.refreshToken();
    }
  }

  // =============================================================
  // 3. WARD FILTER LOGIC
  // =============================================================

  initializeWards(): void {
    const wards = new Set<string>();
    this.data.forEach(patient => {
      if (patient.ward) {
        wards.add(patient.ward);
      }
    });
    this.availableWards = Array.from(wards).sort();
  }

  toggleWardFilter(): void {
    this.isWardFilterOpen = !this.isWardFilterOpen;
    // Close other filters if open
    this.isDropdownOpen = false;
  }

  filterByWard(ward: string | null): void {
    this.selectedWard = ward;
    this.isWardFilterOpen = false;

    if (ward === null) {
      // Reset
      this.displaydashboardData();
    } else {
      // Client-side filtering for immediate feedback (or replace with API call if supported)
      this.data = this.allPatientsData.filter(patient => patient.ward === ward);
      this.calculateRiskCounts(this.data);
      this.totalRows = this.data.length;
      this.updatePagesArray();
    }
  }

  // =============================================================
  // 4. ACCORDION / CARD TIMELINE LOGIC
  // =============================================================

  toggleCardTimeline(item: any, event: MouseEvent): void {
    // Prevent toggling if user clicks a button/checkbox/link inside the card
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.closest('.card-checkbox') || target.closest('.status-badge')) {
      return;
    }

    // Toggle selection
    item.isSelected = !item.isSelected;

    // Lazy load graph data if not present
    if (item.isSelected && !item.timelineGraphData) {
      this.loadTimelineDataForCard(item);
    }

    // Save state to local storage
    const visualizeClickedObj = JSON.parse(localStorage.getItem('visualizeClickedObj')) || {};
    if (item.isSelected) {
      visualizeClickedObj[item.patient_number] = { isSelected: true };
    } else {
      delete visualizeClickedObj[item.patient_number];
    }
    localStorage.setItem('visualizeClickedObj', JSON.stringify(visualizeClickedObj));

    // Global flag for timeline visibility
    this.displayTimeline = this.data.some(d => d.isSelected);
  }

  loadTimelineDataForCard(item: any): void {
    if (this.auth.isTokenValid()) {
      const params = new HttpParams().set('patient_numbers', item.patient_number);
      this.patientService.graphData(params).subscribe((res) => {
        const timelineGraphData = res[item.patient_number];
        if (timelineGraphData) {
          item.timelineGraphData = timelineGraphData;
          const timeGraph = timelineGraphData.Timezone;
          this.formattingTime = this.formatData(item.patient_number.split('-')[1], timeGraph);
          item.timeGraphId = this.formattingTime;
        }
      });
    }
  }

  closeAllgraphs() {
    this.displayTimeline = false;
    this.data.forEach(item => {
      item.timelineGraphData = null;
      item.isSelected = false;
      item.selected = false;
    });
    localStorage.setItem('visualizeClickedObj', JSON.stringify({}));
    // Also clear checkbox state if desired
    // localStorage.setItem('checkboxState', JSON.stringify({})); 
  }

  // =============================================================
  // 5. LEGACY FILTERS (DATA STATUS / RISK STATUS)
  // =============================================================

  closeFilter() {
    if (this.auth.isTokenValid()) {
      this.isDropdownOpen = false;
      this.activeDropdown = '';
    } else {
      this.auth.refreshToken(this.routerStateSnapshot.url);
    }
  }

  filterData(column: string, listName: string) {
    // Note: listName is passed from HTML but we use the typed arrays in class
    let selectedCheckboxes: any[] = [];

    if (column === 'datastatus') {
      selectedCheckboxes = this.selectedDataStatusCheckboxes;
    } else if (column === 'risk_type') {
      selectedCheckboxes = this.selectedRiskStatusCheckboxes;
    }

    if (this.auth.isTokenValid()) {
      const selectedValues = selectedCheckboxes.map(item => item.name).join(',');

      this.filterColumn = column;
      this.filterKey = selectedValues;
      this.filterParams = { filtercolumn: column, filterkey: selectedValues };

      this.patientService.listOfPatients(this.buildParamsWithFiltering()).subscribe((res) => {
        this.data = res.data;
        this.totalRows = res.totalRows;
        this.currentPage = 1;
        this.updatePagesArray();
        this.calculateRiskCounts(this.data);
        this.restoreCheckboxStates();
      });

      this.isDropdownOpen = false;
    } else {
      this.auth.refreshToken();
    }
  }

  handleCheckboxSelection(column: string, item: any) {
    const filterStore = column === 'datastatus' ? 'selectedDataFilters' : 'selectedRiskFilters';
    const currentFilters = JSON.parse(localStorage.getItem(filterStore)) || {};

    if (item.selected) {
      currentFilters[item.name] = true;
      if (column === 'datastatus') this.selectedDataStatusCheckboxes.push(item);
      else this.selectedRiskStatusCheckboxes.push(item);
    } else {
      delete currentFilters[item.name];
      if (column === 'datastatus') this.selectedDataStatusCheckboxes = this.selectedDataStatusCheckboxes.filter(i => i.name !== item.name);
      else this.selectedRiskStatusCheckboxes = this.selectedRiskStatusCheckboxes.filter(i => i.name !== item.name);
    }
    localStorage.setItem(filterStore, JSON.stringify(currentFilters));
  }

  // =============================================================
  // 6. SORTING LOGIC
  // =============================================================

  sortingData(columnName: any, direction: 'asc' | 'desc') {
    if (this.auth.isTokenValid()) {
      if (columnName === this.sortColumn && direction === this.sortOrder) {
        // Clear sorting
        this.sortColumn = null;
        this.sortOrder = null;
        localStorage.setItem('sortingData', JSON.stringify({}));
        this.displaydashboardData();
        this.removeBackgroundColor();
      } else {
        // Apply sorting
        this.highlightColumn(columnName, direction);
        this.sortParams = { sortcolumn: columnName, sortorder: direction };
        localStorage.setItem('sortingData', JSON.stringify(this.sortParams));
        this.sortColumn = columnName;
        this.sortOrder = direction;

        const params = (this.previousFilterParams.filtercolumn)
          ? this.buildSortParamsWithFilter()
          : this.buildParamsWithSorting();

        this.patientService.listOfPatients(params).subscribe((res) => {
          this.data = res.data;
          this.calculateRiskCounts(this.data);
          this.restoreCheckboxStates();
        });
      }
    }
  }

  private highlightColumn(col: string, dir: 'asc' | 'desc') {
    this.hubIdHighlighted = col === 'hub_id' ? dir : null;
    this.wardHighlighted = col === 'ward' ? dir : null;
    this.BedHighlighted = col === 'bed' ? dir : null;
    this.dataHighlighted = col === 'datastatus' ? dir : null;
    this.riskHighlighted = col === 'risk_type' ? dir : null;
  }

  removeBackgroundColor() {
    this.hubIdHighlighted = null;
    this.wardHighlighted = null;
    this.BedHighlighted = null;
    this.dataHighlighted = null;
    this.riskHighlighted = null;
  }

  // =============================================================
  // 7. PAGINATION & PARAM BUILDERS
  // =============================================================

  buildParamsWithoutSorting() {
    return new HttpParams()
      .set('pageNumber', this.currentPage.toString())
      .set('pageSize', this.itemsPerPage.toString());
  }

  buildParamsWithSorting() {
    return this.buildParamsWithoutSorting()
      .set('sortColumn', this.sortColumn)
      .set('sortOrder', this.sortOrder);
  }

  buildParamsWithFiltering() {
    return this.buildParamsWithoutSorting()
      .set('filterColumn', this.filterColumn)
      .set('filterKey', this.filterKey);
  }

  buildSortParamsWithFilter() {
    let params = this.buildParamsWithoutSorting();
    if (this.filterParams.filtercolumn) {
      params = params.set('filterColumn', this.filterParams.filtercolumn)
        .set('filterKey', this.filterParams.filterkey);
    }
    if (this.sortParams.sortcolumn) {
      params = params.set('sortColumn', this.sortParams.sortcolumn)
        .set('sortOrder', this.sortParams.sortorder);
    }
    return params;
  }

  get totalPages() {
    return Math.ceil(this.totalRows / this.itemsPerPage);
  }

  updatePagesArray() {
    this.pagesArray = new Array(this.totalPages).fill(0).map((_, i) => i + 1);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.displaydashboardData();
    }
  }

  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  previousPage() {
    this.goToPage(this.currentPage - 1);
  }

  // =============================================================
  // 8. HELPERS (Translate, Formatting, Restoring State)
  // =============================================================

  translateRiskStatus(riskStatus: string): string {
    const riskstatus = [
      "At left position for",
      "At right position for",
      "At supine position for",
      "Out Of Bed for",
      "No Data for"
    ];
    const match = riskstatus.find(key => riskStatus.startsWith(key));
    if (match) {
      const timePart = riskStatus.slice(match.length).trim();
      return `${this.translate.instant(match)} ${timePart}`;
    }
    return riskStatus;
  }

  formatData(patient, timezone) {
    let patientBed = patient.substring(0, 3);
    let patientValue = patient.substring(3);
    const match = (timezone || '').match(/(\d{4}-\d{2}-\d{2}), (\d{2}:\d{2}:\d{2}) ([\w+\-]+)/);

    if (!match) {
      console.warn('Malformed timezone string received:', timezone);
      return `${patientBed} ${patientValue}`; // Fallback to just patient info
    }

    let [, datePart, timePart, tzAbbr] = match;
    return `${patientBed} ${patientValue}: ${tzAbbr} ${datePart}_${timePart}`;
  }

  restoreCheckboxStates(): void {
    if (this.auth.isTokenValid()) {
      this.storedCheckboxState = JSON.parse(localStorage.getItem('checkboxState')) || {};
      this.displayTimelineGraph = JSON.parse(localStorage.getItem('visualizeClickedObj')) || {};

      this.data = this.data.map(item => {
        // Restore Checkbox
        item.selected = this.storedCheckboxState[item.patient_number] || false;

        // Restore Expanded Card Graph
        if (this.displayTimelineGraph[item.patient_number]?.isSelected) {
          item.isSelected = true;
          if (!item.timelineGraphData) this.loadTimelineDataForCard(item);
        }
        return item;
      });

      if (this.isDataRestored) return;

      // Restore Filters
      let storedDataFilters = JSON.parse(localStorage.getItem('selectedDataFilters')) || {};
      let storedRiskFilters = JSON.parse(localStorage.getItem('selectedRiskFilters')) || {};

      this.dataStatus.forEach(i => i.selected = storedDataFilters[i.name] || false);
      this.riskStatus.forEach(i => i.selected = storedRiskFilters[i.name] || false);

      this.selectedDataStatusCheckboxes = this.dataStatus.filter(item => item.selected);
      this.selectedRiskStatusCheckboxes = this.riskStatus.filter(item => item.selected);

      if (this.selectedDataStatusCheckboxes.length > 0) this.filterData('datastatus', 'selectedDataStatusCheckboxes');
      if (this.selectedRiskStatusCheckboxes.length > 0) this.filterData('risk_type', 'selectedRiskStatusCheckboxes');

      this.isDataRestored = true;
    }
  }

  onCheckboxChange(item: any) {
    // This is for the card-level checkbox (if you decide to keep it in addition to card click)
    this.storedCheckboxState = JSON.parse(localStorage.getItem('checkboxState')) || {};
    if (!item.selected) {
      delete this.storedCheckboxState[item.patient_number];
      item.isSelected = false; // collapse if unchecked
    } else {
      this.storedCheckboxState[item.patient_number] = true;
    }
    localStorage.setItem('checkboxState', JSON.stringify(this.storedCheckboxState));
  }

  seekHelp(event: MouseEvent, item?: any) {
    event.stopPropagation(); // Prevent card toggle
    this.router.navigate(['/help']);
  }

  ngOnDestroy() {
    if (this.dataSubscription) this.dataSubscription.unsubscribe();
  }
}